// ─── Skeleton.jsx ─────────────────────────────────────────────────────────────
// Loading skeleton components for tables, cards, and stat blocks.
// Import as: import { SkeletonTable, SkeletonCard, SkeletonStats } from "./Skeleton";

import { useTheme } from "./theme";

const SHIMMER = `
  @keyframes _crg_shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

function shimmerStyle(C, extra = {}) {
  return {
    background: `linear-gradient(90deg, ${C.border} 25%, ${C.borderLight} 50%, ${C.border} 75%)`,
    backgroundSize: "200% 100%",
    animation: "_crg_shimmer 1.4s ease-in-out infinite",
    borderRadius: 4,
    ...extra,
  };
}

export function SkeletonBar({ width = "100%", height = 14, style = {} }) {
  const C = useTheme();
  return (
    <>
      <style>{SHIMMER}</style>
      <div style={{ width, height, ...shimmerStyle(C, style) }} />
    </>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }) {
  const C = useTheme();
  return (
    <div>
      <style>{SHIMMER}</style>
      {/* Header row */}
      <div style={{ display: "flex", gap: 8, padding: "10px 0",
        borderBottom: `1px solid ${C.border}`, marginBottom: 2 }}>
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} style={{ flex: 1, height: 10, ...shimmerStyle(C, { opacity: 0.5 }) }} />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{ display: "flex", gap: 8, padding: "13px 0",
          borderBottom: `1px solid ${C.border}` }}>
          {Array.from({ length: cols }).map((_, c) => (
            <div key={c} style={{
              flex: 1, height: 12,
              ...shimmerStyle(C, { opacity: Math.max(0.15, 1 - r * 0.14) }),
            }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCard({ lines = 3, style = {} }) {
  const C = useTheme();
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: 20, ...style }}>
      <style>{SHIMMER}</style>
      <div style={{ width: "55%", height: 16, marginBottom: 14, ...shimmerStyle(C) }} />
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} style={{
          width: `${90 - i * 14}%`, height: 11, marginBottom: 9,
          ...shimmerStyle(C),
        }} />
      ))}
    </div>
  );
}

export function SkeletonStats({ count = 4 }) {
  const C = useTheme();
  return (
    <div style={{ display: "grid",
      gridTemplateColumns: `repeat(${count}, 1fr)`, gap: 14, marginBottom: 24 }}>
      <style>{SHIMMER}</style>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "18px 20px", borderTop: `2px solid ${C.border}` }}>
          <div style={{ width: "60%", height: 10, marginBottom: 10, ...shimmerStyle(C) }} />
          <div style={{ width: "40%", height: 30, marginBottom: 8, ...shimmerStyle(C) }} />
          <div style={{ width: "70%", height: 10, ...shimmerStyle(C) }} />
        </div>
      ))}
    </div>
  );
}